import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import AnalyzerInput from "@/components/AnalyzerInput";
import HeroScoreCard from "@/components/HeroScoreCard";
import { useExperiment } from "@/hooks/useExperiment";

const CHECKS = ["AI Website Development", "Website Modernization", "SEO", "Analytics", "Automation", "AI Integration"];

const fade = (d) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, delay: d, ease: [0.16, 1, 0.3, 1] },
});

export default function Hero() {
  // Conversion Lab: the "home_hero" experiment may override the headline / CTA.
  const { content, track } = useExperiment("home_hero");
  return (
    <section className="relative hero-bg pt-28 md:pt-36 pb-16 md:pb-20 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-10 items-center">
          <div>
            <motion.p {...fade(0)} className="overline">Build • Modernize • Automate • Measure • Grow</motion.p>
            <motion.h1 {...fade(0.08)} data-testid="hero-headline" className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05] mt-4">
              {content.headline
                ? content.headline
                : (<>Turn Your Website Into an <span className="text-brand">Intelligent Business Platform</span></>)}
            </motion.h1>
            <motion.p {...fade(0.16)} className="text-lg text-slate-500 mt-6 max-w-xl leading-relaxed">
              WebsiteTech.pro builds and modernizes websites using AI, SEO, analytics, automation and
              interactive technology designed to grow your business.
            </motion.p>
            {/* PRIMARY CTA — one job: audit the visitor's site. */}
            <motion.div {...fade(0.24)} id="analyzer" className="mt-8 scroll-mt-28">
              <AnalyzerInput />
            </motion.div>
            {/* SECONDARY ROUTE — talk to a human advisor if an automated audit isn't the right fit. */}
            <motion.div {...fade(0.28)} className="mt-4">
              <Link
                to="/contact?topic=growth-advisor"
                onClick={track}
                data-testid="hero-secondary-cta"
                className="inline-flex items-center gap-2 text-sm font-semibold text-brand hover:gap-3 transition-all"
              >
                {content.cta || "Prefer to talk? Book a 20-minute Growth Advisor call"} <ArrowRight size={15} />
              </Link>
            </motion.div>
            <motion.div {...fade(0.32)} className="flex flex-wrap gap-x-6 gap-y-2.5 mt-8">
              {CHECKS.map((c) => (
                <div key={c} className="flex items-center gap-2 text-sm text-slate-600">
                  <span className="w-4 h-4 rounded-full bg-green-100 flex items-center justify-center">
                    <Check size={11} className="text-green-600" strokeWidth={3} />
                  </span>
                  {c}
                </div>
              ))}
            </motion.div>
          </div>

          <div className="lg:pl-6">
            <HeroScoreCard />
          </div>
        </div>
      </div>
    </section>
  );
}
